import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { Loader2 } from "lucide-react";
import { getAuth } from "firebase/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useTRPC } from "@/trpc/client";
import { useChatApiUrl } from "@/hooks/use-chat-api-url";
import { useSalesInsightThread } from "@/hooks/queries/use-sales-insight-threads";
import { useMessageFeedback } from "@/hooks/queries/use-message-feedback";
import { ChatMessage, type ChatUIMessage } from "./ChatMessage";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { ChatStarters } from "./ChatStarters";
import { ChatLanding } from "./ChatLanding";
import { ChatComposer } from "./ChatComposer";
import { ChatScopeHeader } from "./ChatScopeHeader";
import { AssessmentSessionDialog } from "./AssessmentSessionDialog";
import {
  useSalesTeamsMember,
  useSalesTeamsMembers,
} from "@/hooks/queries/use-sales-teams";
import type { ChatScope } from "./scope";
import { scopeKey } from "./scope";
import type { CurrentTopicKey, TopicKey } from "./topics";
import type { IntelligenceTopicRecord } from "@graftus/schema";
import {
  TIME_DIVIDER_THRESHOLD_MS,
  formatDividerLabel,
  parseMessageTime,
} from "./message-time";
import { LogEvents, track } from "@/analytics/track";
import { useProfile } from "@/context/ProfileContext";
import { usePulseCheckInNudge } from "@/hooks/use-pulse-check-in-nudge";
import type { SalesTeamsMemberRecord } from "@graftus/schema";
import { useConnectedApps } from "@/hooks/queries/use-apps";
import {
  extractAiSuggestions,
  formatToolName,
  isToolDone,
  isToolPart,
  normalizeToolPart,
  parseAssistantParts,
  type ToolPartLike,
} from "./chat-tool-utils";
import { deriveQuickActions } from "./quick-actions";
import { MeetingTimePicker } from "./MeetingTimePicker";
import { cn } from "@/lib/utils";
import type { AnserTip } from "@/components/coach/anser-tip";
import type { RailStarter } from "@/components/coach/rail-starter";
import { RailStarterList } from "@/components/coach/RailStarterList";

interface Props {
  businessId: string;
  scope: ChatScope;
  onScopeChange: (scope: ChatScope) => void;
  topicKey: TopicKey;
  onTopicChange: (key: CurrentTopicKey) => void;
  threadId: string | null;
  onThreadCreated: (threadId: string) => void;
  onThreadDeleted: () => void;
  /**
   * Resumes an earlier conversation picked from the page header's history
   * list (GRA-255). `rail` variant has its own picker in `CoachRail`, so
   * this only matters for the full-page `/coach` route.
   */
  onSelectThread?: (threadId: string) => void;
  topics: IntelligenceTopicRecord[];
  /** Hide org-wide scope when the user only has subtree access. */
  hideOrganizationScopeOption?: boolean;
  /** Toggles the context inspector drawer. */
  onOpenContext?: () => void;
  /** Starts a new chat (clears thread). */
  onNewChat?: () => void;
  /** Message submitted from the quick-bar on another view; auto-sent once on mount. */
  initialMessage?: string | null;
  /**
   * Called right after `initialMessage` has been sent so the owner can clear
   * it; otherwise a remount (rail closed and reopened, sheet dismissed) would
   * send the same message again into the now-existing thread.
   */
  onInitialMessageConsumed?: () => void;
  /**
   * `rail` drops the internal `ChatScopeHeader` and narrows the column so the
   * docked coach rail (4a) can supply its own header/context chrome. Defaults
   * to `page` (the full-width `/coach` route).
   */
  variant?: "page" | "rail";
  /** Generated starters shown on an empty rail thread (4b). Rail variant only. */
  railStarters?: RailStarter[];
  /** Typed Anser tips for the empty panel (GRA-149); take precedence over starters. */
  railTips?: AnserTip[];
  /** The quick-question chips under the tips. Rail variant only. */
  railQuickQuestions?: string[];
  /**
   * Replaces the tip list on an empty rail thread (the home page's Idag
   * panel). Receives the composer prefill so its CTAs can hand over a prompt.
   */
  railEmptyState?: (prefill: (prompt: string) => void) => ReactNode;
}

export function IntelligenceChat({
  businessId,
  scope,
  onScopeChange,
  topicKey,
  onTopicChange,
  threadId,
  onThreadCreated,
  onThreadDeleted,
  onSelectThread,
  topics,
  hideOrganizationScopeOption = false,
  onOpenContext,
  onNewChat,
  initialMessage,
  onInitialMessageConsumed,
  variant = "page",
  railStarters,
  railTips,
  railQuickQuestions,
  railEmptyState,
}: Props) {
  const isRail = variant === "rail";
  const { t } = useTranslation("intelligence");
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { businessProfile, memberRole } = useProfile();
  const pulseCheckInNudge = usePulseCheckInNudge(businessProfile, memberRole);

  const threadQuery = useSalesInsightThread(businessId, threadId);
  const feedback = useMessageFeedback(businessId, threadId);
  const upsertThread = useMutation(
    trpc.salesTeams.upsertThread.mutationOptions(),
  );
  const deleteThread = useMutation(
    trpc.salesTeams.deleteThread.mutationOptions(),
  );

  const apiScope: "business" | "team" | "member" = scope.kind;
  const salesTeamId =
    scope.kind === "team"
      ? scope.salesTeamId
      : scope.kind === "member"
        ? scope.salesTeamId
        : null;
  const memberId = scope.kind === "member" ? scope.memberId : null;

  // useChat caches the Chat instance via useRef and ignores later transport
  // changes, so a fresh useMemo transport is silently dropped. Read the body
  // fields from a ref that we keep in sync with the latest props.
  // The id a new conversation will be saved under, chosen before its first
  // message so every turn, the first included, carries the same id to the
  // server and groups as one thread in the traces (GRA-66). Replaced whenever
  // a new, empty conversation starts.
  const pendingThreadIdRef = useRef<string>(newConversationId());
  const bodyRef = useRef({
    businessId,
    apiScope,
    salesTeamId,
    memberId,
    topicKey,
    threadId,
    conversationId: threadId ?? pendingThreadIdRef.current,
  });
  bodyRef.current = {
    businessId,
    apiScope,
    salesTeamId,
    memberId,
    topicKey,
    threadId,
    conversationId: threadId ?? pendingThreadIdRef.current,
  };

  // apiChat, or agent-brain when this business is flagged in (docs/ai-platform/09, 09f).
  const { url: chatApiUrl } = useChatApiUrl(businessId);
  const transport = useMemo(
    () =>
      new DefaultChatTransport<ChatUIMessage>({
        api: chatApiUrl,
        headers: async (): Promise<Record<string, string>> => {
          const user = getAuth().currentUser;
          if (!user) return {};
          const token = await user.getIdToken();
          return { Authorization: `Bearer ${token}` };
        },
        body: () => {
          const b = bodyRef.current;
          return {
            businessId: b.businessId,
            scope: b.apiScope,
            salesTeamId: b.salesTeamId,
            memberId: b.memberId,
            topicKey: b.topicKey,
            language: (i18n.language.split("-")[0] as "sv" | "en") ?? "en",
            ...(b.threadId
              ? { threadId: b.threadId }
              : { conversationId: b.conversationId }),
          };
        },
      }),
    // A new transport only when the endpoint changes (the flag activating); scope and
    // thread changes flow through bodyRef without remounting the hook.
    [chatApiUrl],
  );

  const {
    messages,
    sendMessage,
    setMessages,
    status,
    error,
    clearError,
    stop,
  } = useChat<ChatUIMessage>({ transport });

  // Load messages when the thread or scope changes. Intentionally omit
  // `topicKey` from the key so topic-only changes do not replace the in-memory
  // transcript. Topic for the API lives in `bodyRef`.
  const lastLoadedRef = useRef<string>("");
  useEffect(() => {
    // Do not advance the load key while thread metadata is still fetching: scope
    // may briefly reflect the no-org default (team/member) and match the real
    // thread scope, which would skip hydration once messages arrive.
    if (threadId && threadQuery.isLoading) {
      // If this threadId was just created by our own persist(), adopt the new
      // key immediately so that when the query resolves the key-match guard
      // prevents setMessages from overwriting the already-correct in-memory
      // messages with an identical Firestore copy.
      const key = `${threadId}|${scopeKey(scope)}`;
      if (
        threadId === justCreatedThreadId.current &&
        lastLoadedRef.current !== key
      ) {
        lastLoadedRef.current = key;
        justCreatedThreadId.current = null;
      }
      return;
    }

    const key = `${threadId ?? "new"}|${scopeKey(scope)}`;
    if (lastLoadedRef.current === key) return;
    lastLoadedRef.current = key;
    if (threadId && threadQuery.data?.messages?.length) {
      setMessages(threadQuery.data.messages as unknown as ChatUIMessage[]);
    } else {
      if (!threadId) pendingThreadIdRef.current = newConversationId();
      setMessages([]);
    }
  }, [threadId, threadQuery.data, threadQuery.isLoading, scope, setMessages]);

  const isStreaming = status === "submitted" || status === "streaming";

  const canSuggestFromThread = useMemo(
    () =>
      messages.some((m) => m.role === "user") &&
      messages.some((m) => m.role === "assistant"),
    [messages],
  );

  const persist = async (next: ChatUIMessage[]) => {
    if (!next.length) return;
    try {
      // Preserve existing per-message `createdAt`s (from prior persists or
      // server hydration) so timestamp dividers stay accurate across
      // reloads. Messages missing a timestamp get a monotonic fallback in
      // this batch so a fresh user+assistant pair still sorts correctly
      // and shares roughly the same instant.
      const fallbackBase = Date.now();
      let fallbackOffset = 0;
      const messagesPayload = next.map((m) => {
        const existing = parseMessageTime(m as { createdAt?: unknown });
        const createdAt =
          existing !== null
            ? new Date(existing).toISOString()
            : new Date(fallbackBase + fallbackOffset++).toISOString();
        return {
          id: m.id,
          role: m.role,
          // Firestore rejects `undefined` field values, and AI SDK parts often
          // include optional fields like `state` / `providerMetadata` set to
          // undefined. JSON round-trip drops them along with any non-JSON
          // values that we would not be able to replay anyway.
          parts: stripUndefined(m.parts) as unknown[],
          // Round-trip the assistant's metadata (promptVersion, modelId,
          // contextFingerprint, topicKey emitted by the chat handler) so
          // feedback submitted on rehydrated threads can still be tagged
          // with the prompt iteration that produced the answer.
          metadata: m.metadata ? stripUndefined(m.metadata) : null,
          createdAt,
        };
      });
      const res = await upsertThread.mutateAsync({
        businessId,
        // A new conversation is saved under the id its turns were traced
        // with, so the thread and its traces share one id.
        threadId: threadId ?? pendingThreadIdRef.current,
        topicKey,
        scope: apiScope,
        salesTeamId,
        memberId,
        title: deriveTitle(next, t("chat.defaultTitle")),
        messages: messagesPayload,
      });
      qc.invalidateQueries({ queryKey: [["salesTeams", "listThreads"]] });
      const effectiveThreadId = res?.threadId ?? threadId ?? null;
      if (effectiveThreadId) {
        qc.invalidateQueries({
          queryKey: trpc.salesTeams.getThread.queryKey({
            businessId,
            threadId: effectiveThreadId,
          }),
        });
      }
      if (res?.threadId && !threadId) {
        justCreatedThreadId.current = res.threadId;
        onThreadCreated(res.threadId);
      }
    } catch (e) {
      console.error("persist thread failed", e);
    }
  };

  const previousStatus = useRef(status);
  useEffect(() => {
    if (
      (previousStatus.current === "streaming" ||
        previousStatus.current === "submitted") &&
      status === "ready" &&
      messages.length > 0
    ) {
      void persist(messages);
    }
    previousStatus.current = status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, messages.length]);

  // ---------------------------------------------------------------------------
  // Auto-scroll: keep the viewport pinned to the latest token while a response
  // streams in, but only when the user is already "near the bottom". If they've
  // scrolled up to read history, don't yank them back down.
  // ---------------------------------------------------------------------------
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const justCreatedThreadId = useRef<string | null>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 80;
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  // A signature that changes on every token append (parts text length grows
  // even while messages.length stays the same), so this effect runs during
  // streaming, not just on message boundaries.
  const contentSignature = useMemo(
    () =>
      messages
        .map(
          (m) =>
            `${m.id}:${m.role}:${m.parts.reduce(
              (acc, p) => acc + (p.type === "text" ? p.text.length : 0),
              0,
            )}`,
        )
        .join("|"),
    [messages],
  );

  useEffect(() => {
    if (stickToBottomRef.current) scrollToBottom();
  }, [contentSignature, isStreaming]);

  // Snap to the bottom and re-engage stickiness when the active thread or
  // scope changes (e.g. picking a saved thread from the rail).
  useEffect(() => {
    stickToBottomRef.current = true;
    requestAnimationFrame(scrollToBottom);
  }, [threadId, topicKey, scopeKey(scope), threadQuery.isLoading]);

  // `override` lets callers force-update the body fields synchronously
  // before the request fires when topic/scope changes in the same handler
  // as send (rare); `bodyRef` is otherwise synced each render.
  const handleSend = (
    text: string,
    override?: Partial<{
      topicKey: CurrentTopicKey;
      apiScope: "business" | "team" | "member";
      salesTeamId: string | null;
      memberId: string | null;
    }>,
  ) => {
    if (!text.trim() || isStreaming) return;
    if (override) {
      bodyRef.current = { ...bodyRef.current, ...override };
    }
    const b = bodyRef.current;
    track({
      event: LogEvents.IntelligenceMessageSent.name,
      channel: LogEvents.IntelligenceMessageSent.channel,
      topic_key: b.topicKey,
      scope: b.apiScope,
      has_thread: Boolean(threadId),
      // "Active managers per week" (GRA-217): asking Anser.
      role: memberRole,
      is_manager: memberRole === "owner" || memberRole === "admin",
    });
    lastSentTextRef.current = text.trim();
    stickToBottomRef.current = true;
    void sendMessage({ text });
  };

  const lastSentTextRef = useRef<string>("");
  const initialMessageConsumed = useRef(false);
  useEffect(() => {
    if (
      initialMessage &&
      !initialMessageConsumed.current &&
      !threadQuery.isLoading &&
      messages.length === 0
    ) {
      initialMessageConsumed.current = true;
      handleSend(initialMessage);
      onInitialMessageConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, threadQuery.isLoading, messages.length]);

  const handleDeleteThread = async () => {
    if (threadId) {
      try {
        await deleteThread.mutateAsync({ businessId, threadId });
        track({
          event: LogEvents.IntelligenceThreadDeleted.name,
          channel: LogEvents.IntelligenceThreadDeleted.channel,
          topic_key: topicKey,
          scope: apiScope,
        });
        qc.invalidateQueries({ queryKey: [["salesTeams", "listThreads"]] });
        qc.invalidateQueries({
          queryKey: trpc.salesTeams.getThread.queryKey({
            businessId,
            threadId,
          }),
        });
      } catch {
        // toast handled elsewhere
      }
    }
    setMessages([]);
    lastLoadedRef.current = "";
    onThreadDeleted();
  };

  const memberQuery = useSalesTeamsMember(
    businessId,
    scope.kind === "member" ? scope.memberId : undefined,
  );

  const rosterSalesTeamId =
    scope.kind === "team"
      ? scope.salesTeamId
      : scope.kind === "member" && scope.salesTeamId
        ? scope.salesTeamId
        : undefined;
  const membersForRoster = useSalesTeamsMembers(businessId, rosterSalesTeamId);
  const memberRoster = useMemo(() => {
    const rows = membersForRoster.data ?? [];
    const m = new Map<string, SalesTeamsMemberRecord>();
    for (const row of rows) m.set(row.id, row);
    return m;
  }, [membersForRoster.data]);

  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);

  const composerPrefillNonce = useRef(0);
  const [composerPrefill, setComposerPrefill] = useState<{
    text: string;
    nonce: number;
  } | null>(null);

  const contextLabel = useMemo(() => {
    if (scope.kind === "business") return t("scope.organization");
    if (scope.kind === "team") return t("scope.team");
    return memberQuery.data?.name ?? t("scope.member");
  }, [scope, memberQuery.data, t]);

  const { data: connectedAppsData } = useConnectedApps(businessId);
  const connectedAppIds = useMemo(
    () =>
      (connectedAppsData ?? [])
        .filter((a) => a.connected)
        .map((a) => a.id as string),
    [connectedAppsData],
  );

  const memberName =
    scope.kind === "member" ? memberQuery.data?.name : undefined;

  const lastAssistantMessage = !isStreaming
    ? [...messages].reverse().find((m) => m.role === "assistant")
    : null;

  const quickActions = useMemo(() => {
    if (!lastAssistantMessage) return [];
    const aiSuggestions = extractAiSuggestions(lastAssistantMessage);
    if (aiSuggestions.length > 0) return aiSuggestions;
    const { visibleTools } = parseAssistantParts(lastAssistantMessage, {
      isStreaming: false,
    });
    return deriveQuickActions(
      visibleTools,
      scope,
      topicKey,
      {
        memberName,
        connectedAppIds,
      },
      t,
    );
  }, [
    lastAssistantMessage?.id,
    scope,
    topicKey,
    memberName,
    connectedAppIds,
    t,
  ]);

  // During streaming, find the active (non-done) tool in the last assistant message so
  // ThinkingIndicator can show a contextual label instead of a generic "Tänker…".
  const activeToolLabel = useMemo(() => {
    if (!isStreaming) return undefined;
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.role !== "assistant") return undefined;
    for (const part of lastMsg.parts) {
      if (!isToolPart(part as { type?: string })) continue;
      const normalized = normalizeToolPart(part as ToolPartLike);
      if (!isToolDone(normalized.state)) {
        const toolName = normalized.toolName;
        return t(`tool.labels.${toolName}`, {
          defaultValue: formatToolName(toolName),
        });
      }
    }
    return undefined;
  }, [isStreaming, messages, t]);

  // Show the time picker only when the user explicitly asked to book/schedule
  // AND the agent hasn't already executed the booking. Detection: if the last
  // assistant turn ran COMPOSIO_MULTI_EXECUTE_TOOL the action is complete and
  // the picker would be redundant.
  const userAskedForBooking = useMemo(() => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return false;
    const text = lastUser.parts
      .filter((p): p is { type: "text"; text: string } => p.type === "text")
      .map((p) => p.text)
      .join(" ");
    if (
      !/boka|bokning|schemalägg|boka in|book.*(meeting|möte)|schedule/i.test(
        text,
      )
    ) {
      return false;
    }
    if (lastAssistantMessage) {
      // Only suppress the picker if COMPOSIO_MULTI_EXECUTE_TOOL was used to
      // actually CREATE something (e.g. GOOGLECALENDAR_CREATE_EVENT). Using it
      // to LIST/GET events (availability check) should not hide the picker.
      const booked = lastAssistantMessage.parts.some((part) => {
        const p = part as {
          type?: string;
          toolName?: string;
          input?: Record<string, unknown>;
        };
        if (p.toolName !== "COMPOSIO_MULTI_EXECUTE_TOOL") return false;
        const action = String(p.input?.action ?? "").toUpperCase();
        return (
          action.includes("CREATE") ||
          action.includes("INSERT") ||
          action.includes("ADD")
        );
      });
      if (booked) return false;
    }
    return true;
  }, [messages, lastAssistantMessage]);

  const threadLoading = Boolean(
    threadQuery.isLoading &&
    threadId &&
    threadId !== justCreatedThreadId.current,
  );
  // Full-page empty chat: greeting + composer in the middle, no bottom bar.
  const showLanding = !isRail && !threadLoading && messages.length === 0;

  const prefillFromStarter = (prompt: string) => {
    composerPrefillNonce.current += 1;
    setComposerPrefill({ text: prompt, nonce: composerPrefillNonce.current });
  };

  const composer = (
    <ChatComposer
      scope={scope}
      onSend={handleSend}
      onStop={stop}
      onAttachSession={() => setSessionDialogOpen(true)}
      busy={isStreaming || (Boolean(threadId) && threadQuery.isLoading)}
      isStreaming={isStreaming}
      canSuggestFromThread={canSuggestFromThread}
      connectedAppIds={connectedAppIds}
      composerPrefill={composerPrefill}
      landing={showLanding}
    />
  );

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <div className="relative min-h-0 flex-1">
        {!isRail && (
          <ChatScopeHeader
            businessId={businessId}
            scope={scope}
            onScopeChange={onScopeChange}
            scopeLocked={Boolean(threadId)}
            topicKey={topicKey}
            onTopicChange={onTopicChange}
            topics={topics}
            topicLocked={Boolean(threadId)}
            threadId={threadId}
            onDeleteThread={handleDeleteThread}
            onSelectThread={onSelectThread}
            hideOrganizationOption={hideOrganizationScopeOption}
            onOpenContext={onOpenContext}
            onNewChat={onNewChat}
            className="absolute inset-x-0 top-0"
          />
        )}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="absolute inset-0 z-0 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent] [&::-webkit-scrollbar]:w-0.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/40"
        >
          <div
            className={cn(
              "mx-auto w-full",
              isRail && "px-3 pt-4 pb-44",
              !isRail && !showLanding && "max-w-[720px] px-4 pt-12 pb-44",
              showLanding &&
                "flex min-h-full max-w-[760px] flex-col px-4 pt-12",
            )}
          >
            {threadLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : messages.length === 0 ? (
              isRail ? (
                railEmptyState ? (
                  railEmptyState(prefillFromStarter)
                ) : (
                  <RailStarterList
                    tips={railTips}
                    starters={railStarters ?? []}
                    quickQuestions={railQuickQuestions}
                    onPick={prefillFromStarter}
                  />
                )
              ) : (
                <ChatLanding
                  subtitle={t("chat.starters.heading", {
                    context: contextLabel,
                  })}
                  composer={composer}
                >
                  <ChatStarters
                    hideHeading
                    contextLabel={contextLabel}
                    topicKey={topicKey}
                    onTopicChange={onTopicChange}
                    topics={topics}
                    showPulseSetupNudge={pulseCheckInNudge.showSetup}
                    pulseCheckInSummary={
                      pulseCheckInNudge.show ? pulseCheckInNudge.summary : null
                    }
                    onPrefillPrompt={prefillFromStarter}
                  />
                </ChatLanding>
              )
            ) : (
              <div className="space-y-6">
                {messages.map((m, i) => {
                  const currentTime = parseMessageTime(
                    m as { createdAt?: unknown },
                  );
                  const previousTime =
                    i > 0
                      ? parseMessageTime(
                          messages[i - 1] as { createdAt?: unknown },
                        )
                      : null;
                  const showDivider =
                    currentTime !== null &&
                    (i === 0 ||
                      (previousTime !== null &&
                        currentTime - previousTime >=
                          TIME_DIVIDER_THRESHOLD_MS));
                  return (
                    <Fragment key={m.id}>
                      {showDivider && (
                        <div className="text-center text-xs text-muted-foreground">
                          {formatDividerLabel(currentTime!)}
                        </div>
                      )}
                      <ChatMessage
                        message={m}
                        isStreaming={
                          isStreaming &&
                          i === messages.length - 1 &&
                          m.role === "assistant"
                        }
                        onDelete={threadId ? handleDeleteThread : undefined}
                        feedback={feedback.byMessageId.get(m.id)}
                        feedbackContext={{
                          businessId,
                          threadId,
                          scope,
                          topicKey,
                        }}
                        businessId={businessId}
                        memberRoster={memberRoster}
                        suppressEmptyFallback={
                          Boolean(error) && i === messages.length - 1
                        }
                      />
                    </Fragment>
                  );
                })}
                {isStreaming &&
                  (messages.length === 0 ||
                    messages[messages.length - 1]?.role !== "assistant") && (
                    <ThinkingIndicator label={activeToolLabel} />
                  )}
                {error && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                    {error.message || t("chat.errorDefault")}
                    {lastSentTextRef.current && (
                      <button
                        type="button"
                        onClick={() => {
                          clearError();
                          handleSend(lastSentTextRef.current);
                        }}
                        className="ml-3 underline"
                      >
                        {t("chat.retry")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={clearError}
                      className="ml-3 underline"
                    >
                      {t("chat.close")}
                    </button>
                  </div>
                )}
                {quickActions.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {quickActions.map((action) => (
                      <button
                        key={action}
                        type="button"
                        onClick={() => {
                          composerPrefillNonce.current += 1;
                          setComposerPrefill({
                            text: action,
                            nonce: composerPrefillNonce.current,
                          });
                        }}
                        className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        {action}
                      </button>
                    ))}
                    {connectedAppIds.includes("GOOGLECALENDAR") &&
                      userAskedForBooking && (
                        <MeetingTimePicker
                          memberName={memberName}
                          onSelect={(text) => {
                            composerPrefillNonce.current += 1;
                            setComposerPrefill({
                              text,
                              nonce: composerPrefillNonce.current,
                            });
                          }}
                        />
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {!showLanding && (
          <div className="absolute bottom-0 left-0 right-0">
            <div
              aria-hidden="true"
              className={cn(
                "pointer-events-none h-7 bg-gradient-to-t to-transparent",
                isRail ? "from-g-bg-shell" : "from-background",
              )}
            />
            {composer}
          </div>
        )}
      </div>

      {scope.kind === "member" && memberQuery.data && (
        <AssessmentSessionDialog
          open={sessionDialogOpen}
          onOpenChange={setSessionDialogOpen}
          businessId={businessId}
          memberId={scope.memberId}
          memberName={memberQuery.data.name}
        />
      )}
    </div>
  );
}

function stripUndefined<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function deriveTitle(messages: ChatUIMessage[], defaultTitle: string): string {
  const firstUser = messages.find((m) => m.role === "user");
  if (!firstUser) return defaultTitle;
  const text = firstUser.parts
    .map((p) => (p.type === "text" ? p.text : ""))
    .join(" ")
    .trim();
  return text.length > 80 ? `${text.slice(0, 77)}…` : text || defaultTitle;
}

/** A new conversation's id: 32 hex characters, safe as a Firestore doc id. */
function newConversationId(): string {
  return crypto.randomUUID().replace(/-/g, "");
}
